import React, { useEffect, useState } from "react";
import { NavigateButton } from "@/components/NavigateButton";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MapPin, Navigation } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Gallery } from "@/components/Gallery";
import { VenueMap } from "@/components/VenueMap";
import { TypeBadge } from "@/components/VenueBits";
import { VenueEventsList } from "@/components/VenueEventsList";
import { EventReviews } from "@/components/EventReviews";
import { HeartButton } from "@/components/HeartButton";
import { VenueShareButtons } from "@/components/ShareButtons";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { Venue } from "@/types";

export default function VenueDetails() {
  const { id } = useParams();
  const { t, lang } = usePrefs();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [v, setV] = useState<Venue | null | false>(null);
  const [booking, setBooking] = useState(false);

  const load = () => api.get(`/venues/${id}`).then((r) => setV(r.data)).catch(() => setV(false));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const book = () => {
    if (!user || user.role !== "customer") {
      toast(t("booking.loginNeeded"));
      if (!user) navigate("/login");
      return;
    }
    setBooking(true);
  };

  const name = v ? (lang === "ar" ? v.name_ar : v.name_en || v.name_ar) : "";
  const desc = v ? (lang === "ar" ? v.description_ar : v.description_en || v.description_ar) : "";
  const hasLoc = v && typeof v.lat === "number" && typeof v.lng === "number";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-5 pb-32 md:pb-12" data-testid="venue-details-page">
        <button data-testid="venue-details-back-button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} className="mb-4 h-10 px-4 inline-flex items-center gap-1.5 rounded-full border bg-card text-sm font-bold hover:bg-accent">
          <ArrowRight className="h-4 w-4 ltr:rotate-180" />
          {t("details.back")}
        </button>
        {v === null && <div className="aspect-[16/9] rounded-[2rem] bg-muted animate-pulse" />}
        {v === false && <p data-testid="venue-not-found" className="text-center text-muted-foreground py-20">{t("details.notFound")}</p>}
        {v && (
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
            <div className="space-y-6">
              <div className="relative">
                <Gallery images={v.gallery?.length ? v.gallery : v.image ? [v.image] : []} alt={name} />
                <HeartButton venueId={v.venue_id} className="absolute top-3 end-3 z-10" />
              </div>
              <section className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <TypeBadge type={v.type} />
                  {v.city && <span className="inline-flex items-center gap-1 text-sm text-muted-foreground font-semibold"><MapPin className="h-4 w-4" />{v.city}</span>}
                </div>
                <h1 data-testid="venue-details-name" className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight">{name}</h1>
                <VenueShareButtons venueId={v.venue_id} name={name} />
              </section>
              {desc && (
                <section className="space-y-2">
                  <h2 className="font-display text-lg font-bold">{t("details.about")}</h2>
                  <p data-testid="venue-details-description" className="text-muted-foreground leading-relaxed">{desc}</p>
                </section>
              )}
            </div>
            <aside className="space-y-5 lg:sticky lg:top-20 self-start">
              <div className="rounded-3xl border bg-card p-5 space-y-4 shadow-sm">
                <h2 className="font-display text-lg font-bold">{t("ev.upcoming")}</h2>
                <VenueEventsList venueId={v.venue_id} />
                <div className="pt-4"><EventReviews venueId={v.venue_id} /></div>
              </div>
              <div className="space-y-3">
                <h2 className="font-display text-lg font-bold">{t("details.location")}</h2>
                {v.address && <p data-testid="venue-details-address" className="text-sm text-muted-foreground">{v.address}</p>}
                {hasLoc ? (
                  <>
                    <VenueMap lat={v.lat} lng={v.lng} className="h-64" />
                    <NavigateButton lat={v.lat} lng={v.lng} name={v.name_ar} testId="venue-open-map-link" className="inline-flex h-10 px-4 items-center gap-2 rounded-full bg-accent text-sm font-bold">
                      <Navigation className="h-4 w-4" />
                      {t("details.openMap")}
                    </NavigateButton>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground rounded-2xl border border-dashed p-4">{t("details.noLocation")}</p>
                )}
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
